<template>
  <section class="tickets">
    <ul
      v-if="sortedBundles.length"
      class="tickets__bundles-list"
    >
      <li
        v-for="(bundleData, displayIndex) in sortedBundles"
        :key="bundleData.bundle.product_family_id"
        :class="{
          'tickets__bundle--selected':
            selectedBundle?.bundleIndex === bundleData.originalIndex &&
            selectedBundle?.travelIndex === travelIndex &&
            selectedBundle?.routeIndex === routeIndex &&
            hasSelectedProducts,
        }"
        class="tickets__bundle"
      >
        <div class="tickets__header">
          <RadioButton
            :id="`bundle_${travelIndex}_${routeIndex}_${displayIndex}`"
            name="journey_bundle_selection"
            :value="bundleData.bundle"
            :modelValue="
              selectedBundle?.bundleIndex === bundleData.originalIndex &&
              selectedBundle?.travelIndex === travelIndex &&
              selectedBundle?.routeIndex === routeIndex &&
              hasSelectedProducts
                ? bundleData.bundle
                : null
            "
            :disabled="!hasSelectedProducts"
            @update:modelValue="handleBundleSelection(bundleData, displayIndex)"
          >
            <div class="tickets__info">
              <span>{{ getMainTicketName(bundleData.bundle) }}</span>
              <div class="tickets__price">
                <span
                  v-if="showOriginalPrice(bundleData.bundle)"
                  class="tickets__original-price"
                >
                  {{ bundleData.bundle.price }},-
                </span>
                <span
                  :class="{
                    'tickets__discount-price': showOriginalPrice(
                      bundleData.bundle
                    ),
                  }"
                >
                  {{ displayPrice(bundleData.bundle) }},-
                </span>
              </div>
            </div>
          </RadioButton>
        </div>
        <ul class="tickets__descriptions">
          <li>
            <svg
              v-if="getTicketStatements(bundleData.bundle).refundable.value"
              class="icon-checked"
            >
              <use :xlink:href="`#${checkedIcon}`" />
            </svg>
            <svg
              v-else
              class="icon-cancel"
            >
              <use :xlink:href="`#${cancelIcon}`" />
            </svg>
            {{ getTicketStatements(bundleData.bundle).refundable.text }}
          </li>
          <li>
            <svg
              v-if="getTicketStatements(bundleData.bundle).changeable.value"
              class="icon-checked"
            >
              <use :xlink:href="`#${checkedIcon}`" />
            </svg>
            <svg
              v-else
              class="icon-cancel"
            >
              <use :xlink:href="`#${cancelIcon}`" />
            </svg>
            {{ getTicketStatements(bundleData.bundle).changeable.text }}
          </li>
        </ul>
      </li>
    </ul>
    <div
      v-else
      class="tickets__bundle"
    >
      <div class="tickets__header">
        {{ $t('travelPlanner.journeyList.tickets.title') }}
      </div>
      <div class="tickets__sold-out">
        {{ getSalesRestrictionMessage }}
      </div>
    </div>
  </section>
</template>

<script lang="ts">
  import { defineComponent, PropType, computed, watch, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import {
    JourneyTicketStatement,
    SearchSelection,
    ExtendedLeg,
  } from '@/types/searchTypes'
  import type { Bundle } from '@/types/journeyTypes'
  import type { PassengerFare } from '@/types/passengerTypes'
  import type { Segment as BookingSegment } from '@/types/bookingTypes'
  import type { Travel, Route, Leg } from '@/types/travelTypes'
  import type { TravelSeat } from '@/types/seatTypes'
  import RadioButton from '@/components/BaseComponents/RadioButton.vue'
  import checkedIcon from '@/assets/icons/checked.svg'
  import cancelIcon from '@/assets/icons/cancel.svg'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useMyTravelStore } from '@/stores/myTravelStore'
  import { useRoute } from 'vue-router'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useErrorStore } from '@/stores/errorStore'
  import { storeToRefs } from 'pinia'

  interface BundleWithIndex {
    bundle: Bundle
    originalIndex: number
  }

  interface BundleItem {
    name: string
    passenger_fares?: PassengerFare[]
  }

  interface ExtendedPassengerFare extends PassengerFare {
    total_price_to_be_paid?: number
  }

  interface BookingSegmentData {
    client_ref: string
    origin: string
    destination: string
    direction: 'outbound' | 'inbound'
    start_validity_date: string
    service_name: string
    service_identifier: string
    items: Array<{
      passenger_id: string
      tariff_code: string
    }>
    additional_tariff_conditions: {
      journey_od: {
        origin: string
        destination: string
      }
    }
  }

  import { normalizeTicketPrice } from '@/utils/priceNormalization'

  export default defineComponent({
    name: 'JourneyListTickets',
    components: {
      RadioButton,
    },
    props: {
      bundles: {
        type: Array as PropType<Bundle[]>,
        required: true,
        default: () => [],
      },
      travelIndex: {
        type: Number,
        required: true,
      },
      routeIndex: {
        type: Number,
        required: true,
      },
      selectedBundle: {
        type: Object as PropType<SearchSelection | null>,
        default: null,
      },
      showOutbound: {
        type: Boolean,
        required: true,
      },
      legs: {
        type: Array as PropType<ExtendedLeg[]>,
        required: true,
        default: () => [],
      },
    },
    emits: ['bundle-selected'],
    setup(props, { emit }) {
      const { t } = useI18n()
      const journeyStore = useJourneyStore()
      const { selectedProducts, selectedBundle } = storeToRefs(journeyStore)
      const myTravelStore = useMyTravelStore()
      const bookingStore = useBookingStore()
      const errorStore = useErrorStore()
      const route = useRoute()

      const isRebooking = computed(() => {
        const fromRebookPath = route.path.toLowerCase().includes('rebook')
        const fromRebookQuery = route.query.isRebooking === 'true'
        const hasRebookingType = !!route.query.rebookingType
        const hasBookingId = !!route.query.bookingId
        const hasEntireJourney = route.query.entireJourney === 'true'
        
        const isInRebooking = (
          (fromRebookPath || fromRebookQuery || hasRebookingType) &&
          (hasBookingId || hasRebookingType)
        )
        
        if (isInRebooking) {
          myTravelStore.setRebookingMode(true);
        }
        return isInRebooking;
      })

      const passengerCounts = computed(() => {
        const counts = {
          adults: 0,
          children: 0,
          infants: 0,
          students: 0,
          seniors: 0,
        }

        journeyStore.passengers.forEach((passenger) => {
          switch (passenger.type) {
            case 'ADT':
              counts.adults++
              break
            case 'CHD':
              counts.children++
              break
            case 'INF':
              counts.infants++
              break
            case 'STU':
              counts.students++
              break
            case 'SEN':
              counts.seniors++
              break
          }
        })

        return counts
      })

      const getTicketStatements = (bundle: Bundle) => {
        const familyId = bundle.product_family_id
        return {
          refundable: {
            text: t(`descriptions.tickets.${familyId}.refundable.text`),
            value:
              t(`descriptions.tickets.${familyId}.refundable.value`) === 'true',
          },
          changeable: {
            text: t(`descriptions.tickets.${familyId}.changeable.text`),
            value:
              t(`descriptions.tickets.${familyId}.changeable.value`) === 'true',
          },
        }
      }

      const showOriginalPrice = (bundle: Bundle) => {
        if (!bundle?.price || !bundle?.original_price) {
          return false
        }
        if (isRebooking.value) {
          return false
        }
        return bundle.price < bundle.original_price
      }

      const displayPrice = (bundle: Bundle) => {
        if (!bundle) {
          return 0
        }
        
        if (isRebooking.value && bundle.hasOwnProperty('total_price_to_be_paid')) {
          return bundle.total_price_to_be_paid || 0
        }
        
        return bundle.price || 0
      }
      
      /**
       * Gets the name of the main ticket item (product_type: "ST") from the bundle
       * Falls back to the first item if no ST item is found
       */
      const getMainTicketName = (bundle: Bundle) => {
        if (!bundle?.items || bundle.items.length === 0) {
          return ''
        }
        
        const mainTicketItem = bundle.items.find(item => item.product_type === 'ST')
        if (mainTicketItem) {
          return mainTicketItem.name
        }
        
        return bundle.items[0].name
      }

      const mapPassengerFare = (
        fare: PassengerFare
      ): ExtendedPassengerFare => ({
        ...fare,
        price: Number(fare.price || 0),
        original_price: Number(fare.original_price || fare.price || 0),
        total_price_to_be_paid: Number(fare.price || 0),
      })

      const sortedBundles = computed<BundleWithIndex[]>(() => {
        return props.bundles
          .map((bundle, index) => ({
            bundle,
            originalIndex: index,
          }))
          .sort((a, b) => {
            // Always show STANDARD first, FLEX second
            if (a.bundle.product_family_id === 'STANDARD') return -1
            if (b.bundle.product_family_id === 'STANDARD') return 1
            return 0
          })
      })

      const getSalesRestrictionMessage = computed(() => {
        if (props.legs?.length) {
          for (const leg of props.legs) {
            if (leg.sales_restrictions?.length) {
              return t(
                `travelPlanner.journeyList.travel.${leg.sales_restrictions[0].reason_code}`
              )
            }
          }
        }
        return t('travelPlanner.journeyList.travel.full')
      })

      const handleBundleSelection = (
        bundleData: BundleWithIndex,
        displayIndex: number
      ) => {
        if (!bundleData) return

        const originalBundleIndex = bundleData.originalIndex
        
        journeyStore.setSelectedBundle({
          bundleIndex: originalBundleIndex,
          travelIndex: props.travelIndex,
          routeIndex: props.routeIndex,
        })
                
        if (isRebooking.value && bundleData.bundle.hasOwnProperty('fee_amount')) {
          const feeAmount = bundleData.bundle.fee_amount || 0;
          if (props.showOutbound) {
            myTravelStore.setOutboundRebookingFee(feeAmount);
          } else {
            myTravelStore.setInboundRebookingFee(feeAmount);
          }
        }

        const selectedTravelData = props.showOutbound
          ? journeyStore.journeySearchResults?.data?.offer?.travels?.find(
              (travel) => travel.direction === 'outbound'
            )
          : journeyStore.journeySearchResults?.data?.offer?.travels?.find(
              (travel) => travel.direction === 'inbound'
            )

        if (!selectedTravelData) {
          errorStore.setError('No travel data found')
          return
        }

        const displayRoute = selectedTravelData.routes[props.routeIndex]
        if (!displayRoute || !displayRoute.legs || displayRoute.legs.length === 0) {
          errorStore.setError('No route or legs found')
          return
        }
        
        const displayLeg = displayRoute.legs[0] as Leg
        const targetServiceId = displayLeg.service_identifier
        const selectedRoute = selectedTravelData.routes.find(route => 
          route.legs && 
          route.legs.length > 0 && 
          route.legs[0].service_identifier === targetServiceId
        ) || displayRoute // Fall back to display route if not found
        
        if (!selectedRoute.legs || selectedRoute.legs.length === 0) {
          errorStore.setError('No legs found in selected route')
          return
        }

        const leg = selectedRoute.legs[0] as Leg
        const serviceIdentifier = leg.service_identifier
        const serviceName = leg.service_name
                
        const ticketDataList = journeyStore.passengers
          .map((passenger) => {
            let totalPrice = 0
            let originalPrice = 0
            let totalPriceToBePaid = 0
            let foundFare = false
            let tariffCode = ''

            bundleData.bundle.items.forEach((item: BundleItem) => {
              const passengerFare = item.passenger_fares?.find(
                (fare: PassengerFare) => fare.passenger_id === passenger.id
              )
              if (passengerFare) {
                foundFare = true
                totalPrice += Number(passengerFare.price || 0)
                originalPrice += Number(passengerFare.original_price || passengerFare.price || 0)
                totalPriceToBePaid += Number(passengerFare.price || 0)
                if (!tariffCode) {
                  tariffCode = passengerFare.tariff_code || ''
                }
              }
            })

            if (!foundFare) {
              errorStore.setError(`No fare found for passenger ${passenger.id}`)
              return null
            }

            const typeMap: Record<string, string> = {
              ADT: t('common.passenger.adult'),
              CHD: t('common.passenger.child'),
              YOU: 'Ungdom',
              STU: t('common.passenger.student'),
              SEN: t('common.passenger.senior'),
              CON: t('common.passenger.conscript'),
              CPN: t('common.passenger.companion'),
              COM: t('common.passenger.companion')
            }
            
            const passengerTypeDisplay = typeMap[passenger.type] || passenger.type
            const passengerCount = journeyStore.passengers.filter(p => p.type === passenger.type).length
            const bundleName = getMainTicketName(bundleData.bundle)
            
            if (totalPrice === 0 && bundleData.bundle.price) {
              totalPrice = Number(bundleData.bundle.price)
              totalPriceToBePaid = totalPrice
            }

            const normalizedPrice = normalizeTicketPrice(totalPrice)
            const normalizedOriginalPrice = normalizeTicketPrice(originalPrice)
            const normalizedTotalPriceToBePaid = normalizeTicketPrice(totalPriceToBePaid)

            return {
              passenger_id: passenger.id,
              passenger_type: passenger.type,
              passenger_type_display: passengerTypeDisplay,
              price: normalizedPrice,
              original_price: normalizedOriginalPrice,
              total_price_to_be_paid: normalizedTotalPriceToBePaid,
              product_family: bundleData.bundle.product_family_id,
              ticket_name: `${passengerCount} ${passengerTypeDisplay} - ${bundleName}`,
              travelIndex: props.travelIndex,
              routeIndex: props.routeIndex,
              departure_timestamp:
                selectedRoute.legs[0].departure_station.departure_timestamp,
              arrival_timestamp:
                selectedRoute.legs[selectedRoute.legs.length - 1]
                  .arrival_station.arrival_timestamp,
              tariff_code: tariffCode || bundleData.bundle.product_family_id,
            }
          })
          .filter(
            (ticket): ticket is NonNullable<typeof ticket> => ticket !== null
          )
        
        if (ticketDataList.length !== journeyStore.passengers.length) {
          console.error('Ticket creation failed. Passengers:', journeyStore.passengers, 'TicketDataList:', ticketDataList);
          errorStore.setError('Failed to create tickets for all passengers')
          return
        }

        try {
          const direction = props.showOutbound ? 'outbound' : 'inbound'
          const bundleCopy = JSON.parse(JSON.stringify(bundleData.bundle))

          bundleCopy.price = normalizeTicketPrice(bundleCopy.price)
          bundleCopy.total_price_to_be_paid = normalizeTicketPrice(
            bundleCopy.total_price_to_be_paid || bundleCopy.price
          )
          bundleCopy.original_price = normalizeTicketPrice(
            bundleCopy.original_price || bundleCopy.price
          )

          if (bundleCopy.items?.[0]?.passenger_fares) {
            bundleCopy.items[0].passenger_fares =
              bundleCopy.items[0].passenger_fares.map(mapPassengerFare)
          }

          myTravelStore.updatePassengerCounts(
            direction,
            journeyStore.passengers,
            ticketDataList
          )

          myTravelStore.updateSelectedTickets(direction, ticketDataList)

          const travelData: Travel = {
            ...selectedTravelData,
            id: selectedTravelData.id || '',
            direction,
            departure_date: selectedTravelData.departure_date || '',
            origin_station: selectedTravelData.origin_station,
            destination_station: selectedTravelData.destination_station,
            routes: [
              {
                ...selectedRoute,
                bundles: [bundleCopy],
                legs: selectedRoute.legs.map((leg) => ({
                  ...leg,
                  id: `${leg.service_identifier}-${leg.departure_station.departure_timestamp}`,
                  seat: leg.seat
                    ? ({
                        seat_number: leg.seat.seat_number,
                        coach: leg.seat.coach,
                        status: 'AVAILABLE',
                      } as TravelSeat)
                    : undefined,
                  passenger_fares: [],
                })) as Leg[],
              },
            ] as Route[],
            bundles: [bundleCopy],
            legs: selectedRoute.legs.map((leg) => ({
              ...leg,
              id: `${leg.service_identifier}-${leg.departure_station.departure_timestamp}`,
              seat: leg.seat
                ? ({
                    seat_number: leg.seat.seat_number,
                    coach: leg.seat.coach,
                    status: 'AVAILABLE',
                  } as TravelSeat)
                : undefined,
              passenger_fares: [],
            })) as Leg[],
          }

          if (direction === 'outbound') {
            myTravelStore.setOriginDestination(
              journeyStore.OriginStation.name,
              journeyStore.destinationStation.name
            )
            myTravelStore.setDates(
              journeyStore.departureDate,
              journeyStore.returnDate
            )
            myTravelStore.setHasReturnTravel(!!journeyStore.returnDate)

            myTravelStore.setOutboundTravel(travelData)

            bookingStore.clearSegmentsByDirection('outbound')
            const segment: BookingSegmentData = {
              client_ref: 'segment_ref_1',
              origin: journeyStore.OriginStation._u_i_c_station_code,
              destination: journeyStore.destinationStation._u_i_c_station_code,
              direction: 'outbound',
              start_validity_date: journeyStore.departureDate,
              service_name: serviceName,
              service_identifier: serviceIdentifier,
              items: journeyStore.passengers.map((passenger, index) => ({
                passenger_id: passenger.id,
                tariff_code:
                  bundleCopy.items[0].passenger_fares[index].tariff_code,
              })),
              additional_tariff_conditions: {
                journey_od: {
                  origin: journeyStore.OriginStation._u_i_c_station_code,
                  destination:
                    journeyStore.destinationStation._u_i_c_station_code,
                },
              },
            }
            bookingStore.addSegment(segment as unknown as BookingSegment)
          } else {
            myTravelStore.setInboundTravel(travelData)

            bookingStore.clearSegmentsByDirection('inbound')
            const segment: BookingSegmentData = {
              client_ref: 'segment_ref_2',
              origin: journeyStore.destinationStation._u_i_c_station_code,
              destination: journeyStore.OriginStation._u_i_c_station_code,
              direction: 'inbound',
              start_validity_date: journeyStore.returnDate,
              service_name: serviceName,
              service_identifier: serviceIdentifier,
              items: journeyStore.passengers.map((passenger, index) => ({
                passenger_id: passenger.id,
                tariff_code:
                  bundleCopy.items[0].passenger_fares[index].tariff_code,
              })),
              additional_tariff_conditions: {
                journey_od: {
                  origin: journeyStore.destinationStation._u_i_c_station_code,
                  destination: journeyStore.OriginStation._u_i_c_station_code,
                },
              },
            }
            bookingStore.addSegment(segment as unknown as BookingSegment)
          }

          emit('bundle-selected', {
            travelIndex: props.travelIndex,
            routeIndex: selectedTravelData.routes.findIndex(route => 
              route.legs && 
              route.legs.length > 0 && 
              route.legs[0].service_identifier === targetServiceId
            ),
            bundleIndex: originalBundleIndex,
            tariffCode: bundleData.bundle.product_family_id,
          })
        } catch (error) {
          errorStore.setError(
            (error as Error).message || 'Unknown error occurred'
          )
        }
      }

      watch(
        () => journeyStore.journeySearchResults,
        () => {
          journeyStore.setSelectedBundle(null)
        },
        { deep: true }
      )

      const hasSelectedProducts = computed(
        () => selectedProducts.value.length > 0
      )

      return {
        sortedBundles,
        handleBundleSelection,
        selectedBundle,
        hasSelectedProducts,
        selectedProducts,
        journeyStore,
        showOriginalPrice,
        displayPrice,
        checkedIcon,
        cancelIcon,
        getTicketStatements,
        passengerCounts,
        getSalesRestrictionMessage,
        getMainTicketName,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .tickets {
    color: $clr-text;
    padding: 1rem 0;

    &__bundles-list {
      list-style-type: none;
      padding: 0;
    }

    &__bundle {
      border: 1px solid $clr-primary-100;
      border-radius: $border-radius;
      margin-bottom: 1.25rem;
      padding: 1.25rem;

      &--selected {
        border-color: $clr-primary;
      }
    }

    &__header {
      @include title-medium;
      border-bottom: 1px solid $clr-primary-100;
      display: flex;
      align-items: center;
      align-content: center;
      padding-bottom: 1rem;
      margin-bottom: 1rem;
    }

    &__info {
      display: flex;
      justify-content: space-between;
      flex-grow: 1;
      gap: 8px;
      width: 100%;
    }

    &__price {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }

    &__original-price {
      font-size: 0.875rem;
      color: $clr-text-disable;
      text-decoration: line-through;
    }

    &__discount-price {
      color: $clr-secondary;
    }

    &__descriptions {
      list-style-type: none;
      padding: 0;
      margin: 0;

      li {
        display: flex;
        align-items: center;
        margin-bottom: 0.5rem;

        svg {
          width: 16px;
          height: 16px;
          min-width: 16px;
          min-height: 16px;
          flex-shrink: 0;
          margin-right: 0.5rem;
        }

        .icon-checked {
          color: $clr-success-hover;
        }
      }
    }

    label {
      @include title-medium;
      padding: 1rem;
      cursor: pointer;
    }

    &__sold-out {
      @include body-large;
      padding: rem(16);
      color: $clr-primary-500;
    }
  }
</style>
